/**
 * Tabela de receitas de craft (PLANO-DIFERENCIAIS Pilar 1 — Economia de Produção).
 * Ingredientes usam slugs REAIS de `src/generated/loot-items.js` / `src/items-data.js`.
 * Dados versionados e puros — sem lógica de execução (fica em `crafting-core.mjs`).
 */

export const PROFESSION_KEYS = Object.freeze(['craft', 'cook', 'mine', 'fish']);

/**
 * `ingredients`: mapa slug → quantidade consumida.
 * `product`: `{ slug, qty }` gerado por craft.
 * `coinsCost`: sink adicional de IdleCoin (opcional, usado em receitas de ponta).
 */
export const RECIPES = Object.freeze([
  Object.freeze({
    id: 'pokeball', profession: 'craft', level: 1, xp: 8,
    ingredients: Object.freeze({ screw: 3, 'bird-beak': 1, 'rubber-ball': 1 }),
    product: Object.freeze({ slug: 'pokeball', qty: 5 }),
  }),
  Object.freeze({
    id: 'greatball', profession: 'craft', level: 5, xp: 18,
    ingredients: Object.freeze({ 'metal-scraps': 2, 'iron-bracelet': 1, magnet: 1 }),
    product: Object.freeze({ slug: 'greatball', qty: 4 }),
  }),
  Object.freeze({
    id: 'superball', profession: 'craft', level: 12, xp: 34,
    ingredients: Object.freeze({ 'air-tank': 1, nail: 2, 'neodymium-magnet': 1 }),
    product: Object.freeze({ slug: 'superball', qty: 3 }),
  }),
  Object.freeze({
    id: 'ultraball', profession: 'craft', level: 20, xp: 60, coinsCost: 1200,
    ingredients: Object.freeze({ 'air-tank': 1, 'drop-electric-box': 1, 'giant-ruby': 1 }),
    product: Object.freeze({ slug: 'ultraball', qty: 2 }),
  }),
  Object.freeze({
    id: 'potion', profession: 'cook', level: 1, xp: 6,
    ingredients: Object.freeze({ 'bag-of-pollen': 2, 'bitten-apple': 1 }),
    product: Object.freeze({ slug: 'potion', qty: 4 }),
  }),
  Object.freeze({
    id: 'great-potion', profession: 'cook', level: 6, xp: 16,
    ingredients: Object.freeze({ mushroom: 1, 'strange-flower': 1, leaves: 1 }),
    product: Object.freeze({ slug: 'great-potion', qty: 3 }),
  }),
  Object.freeze({
    // "antídoto-ish": mapeado para o item real de cura de status (medicine).
    id: 'medicine', profession: 'cook', level: 4, xp: 10,
    ingredients: Object.freeze({ gosme: 1, 'drop-poison-bottle': 1 }),
    product: Object.freeze({ slug: 'medicine', qty: 3 }),
  }),
  Object.freeze({
    id: 'revive', profession: 'cook', level: 14, xp: 40,
    ingredients: Object.freeze({ 'chansey-egg': 1, bulb: 1, 'bag-of-pollen': 2 }),
    product: Object.freeze({ slug: 'revive', qty: 1 }),
  }),
  Object.freeze({
    id: 'fire-stone', profession: 'craft', level: 8, xp: 22,
    ingredients: Object.freeze({ 'drop-essence-fire': 3, ruby: 1 }),
    product: Object.freeze({ slug: 'fire-stone', qty: 1 }),
  }),
  Object.freeze({
    id: 'water-stone', profession: 'craft', level: 8, xp: 22,
    ingredients: Object.freeze({ 'drop-water-gem': 3, 'water-pendant': 1 }),
    product: Object.freeze({ slug: 'water-stone', qty: 1 }),
  }),
  Object.freeze({
    id: 'leaf-stone', profession: 'craft', level: 8, xp: 22,
    ingredients: Object.freeze({ 'naturia-essence': 3, leaves: 1 }),
    product: Object.freeze({ slug: 'leaf-stone', qty: 1 }),
  }),
  Object.freeze({
    id: 'thunder-stone', profession: 'craft', level: 8, xp: 22,
    ingredients: Object.freeze({ 'electric-tail': 3, 'drop-electric-box': 1 }),
    product: Object.freeze({ slug: 'thunder-stone', qty: 1 }),
  }),
  Object.freeze({
    id: 'ice-stone', profession: 'craft', level: 8, xp: 22,
    ingredients: Object.freeze({ 'drop-ice-orb': 3, snowball: 1 }),
    product: Object.freeze({ slug: 'ice-stone', qty: 1 }),
  }),
  Object.freeze({
    // Item de boost novo (fora de items-data por ora); a UI decide como consumi-lo.
    id: 'xp-flask', profession: 'cook', level: 10, xp: 28,
    ingredients: Object.freeze({ 'psychic-spoon': 2, 'future-orb': 1 }),
    product: Object.freeze({ slug: 'xp-flask', qty: 1 }),
  }),
  Object.freeze({
    id: 'loot-amulet', profession: 'craft', level: 16, xp: 50,
    ingredients: Object.freeze({ 'drop-luck-medal': 1, ruby: 2 }),
    product: Object.freeze({ slug: 'loot-amulet', qty: 1 }),
  }),
  Object.freeze({
    id: 'shiny-bait', profession: 'craft', level: 18, xp: 60,
    ingredients: Object.freeze({ pheromone: 1, 'harlequin-shard': 1 }),
    product: Object.freeze({ slug: 'shiny-bait', qty: 1 }),
  }),
  Object.freeze({
    id: 'diving-kit', profession: 'craft', level: 15, xp: 45,
    ingredients: Object.freeze({ 'giant-fin': 1, 'diving-mask': 1, 'air-tank': 1 }),
    product: Object.freeze({ slug: 'diving-kit', qty: 1 }),
  }),
]);

export const RECIPES_BY_ID = Object.freeze(
  Object.fromEntries(RECIPES.map((recipe) => [recipe.id, recipe])),
);

export function recipesForProfession(profession) {
  return RECIPES.filter((recipe) => recipe.profession === profession);
}

/** Fósseis reais de `loot-items.js` → espécie revivida (PLANO-DIFERENCIAIS Pilar 1-E). */
export const FOSSIL_REVIVALS = Object.freeze({
  'dome-fossil': Object.freeze({ species: 'kabuto', coinsCost: 40000 }),
  'helix-fossil': Object.freeze({ species: 'omanyte', coinsCost: 40000 }),
  'old-amber': Object.freeze({ species: 'aerodactyl', coinsCost: 85000 }),
});
